import { useEffect, useRef, useState } from 'react';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';

import { sendSignalRMessage } from '@/lib/signalr';
import { setDisplayIdentity, trackEvent } from '@/lib/errorReporting';
import { DisplayConnection, Playlist } from '../types';
import { getDisplayCodeFromUrl } from '../utils/displayCode';
import { SignalRContext } from '@/providers/app';
import storage from '../utils/storage';

export function useDisplayConnection() {
  const displayCode = getDisplayCodeFromUrl();
  const deviceId = displayCode ? '' : storage.getDeviceId();
  const [displayConnection, setDisplayConnection] = useState<DisplayConnection | null>(null);
  const [playlist, setPlaylist] = useState<Playlist | null>(null);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
  const blockedByServerRef = useRef(false);
  const displayCodeRef = useRef(displayCode);
  const deviceIdRef = useRef(deviceId);
  const reconnectHandlerAttachedRef = useRef<HubConnection | null>(null);

  useEffect(() => {
    displayCodeRef.current = displayCode;
    deviceIdRef.current = deviceId;
  }, [displayCode, deviceId]);

  SignalRContext.useSignalREffect(
    'onDisplayConnect',
    (activeDisplayConnection: DisplayConnection) => {
      blockedByServerRef.current = false;
      setBlockedMessage(null);
      setDisplayConnection(activeDisplayConnection);

      // Tag every subsequent report with the screen it came from. Without this
      // an error tells you something broke somewhere in the fleet, which is
      // most of the diagnostic value gone.
      setDisplayIdentity(activeDisplayConnection);

      void sendSignalRMessage(
        SignalRContext.connection,
        'GetActivePlaylist failed',
        'GetActivePlaylist',
        activeDisplayConnection.displayId
      );
    },
    [displayConnection, playlist]
  );

  SignalRContext.useSignalREffect(
    'onNewDisplay',
    (activeDisplayConnection: DisplayConnection) => {
      console.info('onNewDisplay', activeDisplayConnection);
      blockedByServerRef.current = false;
      setBlockedMessage(null);
      setDisplayConnection(activeDisplayConnection);
    },
    [displayConnection, playlist]
  );

  SignalRContext.useSignalREffect(
    'onDisplayConnectionBlocked',
    (data: { message?: string }) => {
      blockedByServerRef.current = true;
      setDisplayConnection(null);
      setPlaylist(null);
      setBlockedMessage(data?.message ?? 'Displayen blockerades av licensregler.');
    },
    [displayConnection, playlist]
  );

  SignalRContext.useSignalREffect(
    'onGetActivePlaylist',
    (activePlaylist: any) => {
      console.info('onGetActivePlaylist', activePlaylist);
      trackEvent('playlist', 'received playlist', {
        playlistId: activePlaylist?.playlistId,
        itemCount: activePlaylist?.playlistItems?.length,
      });
      setPlaylist(activePlaylist);
    },
    [displayConnection, playlist]
  );

  SignalRContext.useSignalREffect(
    'onRefreshDisplay',
    () => {
      // Recorded so an admin-triggered reload is distinguishable from a crash
      // or an automatic recovery in whatever report comes next.
      console.info('onRefreshDisplay');
      trackEvent('lifecycle', 'reload requested by admin');
      window.location.reload();
    },
    [displayConnection, playlist]
  );

  SignalRContext.useSignalREffect(
    'forceDisconnect',
    (action: string) => {
      if (action === 'reload') {
        storage.setDeviceId('');
        window.location.reload();
      }
    },
    [displayConnection, playlist]
  );

  SignalRContext.useSignalREffect(
    'SetActivePlaylist',
    (displayId: string) => {
      void sendSignalRMessage(SignalRContext.connection, 'GetActivePlaylist failed', 'GetActivePlaylist', displayId);
    },
    [displayConnection, playlist]
  );

  useEffect(() => {
    const connection = SignalRContext.connection;

    if (!connection || reconnectHandlerAttachedRef.current === connection) return;

    const handleReconnect = (_: any) => {
      console.info('Reconnected to SignalR hub');
      if (blockedByServerRef.current) {
        console.info('Reconnect skipped because display is server-blocked.');
        return;
      }

      const currentDisplayCode = displayCodeRef.current;
      const currentDeviceId = deviceIdRef.current;
      if (currentDisplayCode) {
        SignalRContext.invoke('DisplayConnectByCode', currentDisplayCode)?.catch((error: any) => console.error(error));
      } else if (currentDeviceId) {
        SignalRContext.invoke('DisplayConnect', currentDeviceId)?.catch((error: any) => console.error(error));
      }
    };

    connection.onreconnected(handleReconnect);
    reconnectHandlerAttachedRef.current = connection;
  }, [SignalRContext.connection]);

  return {
    hubstate: {
      connected: SignalRContext.connection?.state === HubConnectionState.Connected,
      connecting: SignalRContext.connection?.state === HubConnectionState.Connecting,
      disconnected: SignalRContext.connection?.state === HubConnectionState.Disconnected,
      disconnecting: SignalRContext.connection?.state === HubConnectionState.Disconnecting,
      reconnecting: SignalRContext.connection?.state === HubConnectionState.Reconnecting,
    },
    connection: displayConnection,
    playlist,
    blockedMessage,
  };
}
